<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Automation Rule" avatarImage="/glass/img/automations-overview-automation.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="autoRule.isActive" class="status-active">Enabled</span>
              <span v-if="!autoRule.isActive" class="status-archive">Disabled</span>
            </OverviewBoxListItem>
            
            <OverviewBoxListItem title="Times Actioned">{{autoRule.actionSuccessCount}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Actioned">{{autoRule.dateLastActionSuccess | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{autoRule.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{autoRule.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>
      </div>
      <div class="col-sm-10">
        <TabbedBox :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-1">
              <i class="far fa-rocket"></i> Automation Rule
            </Tab>
            <Tab tabid="tab-2">
              <i class="far fa-clipboard-list"></i> Rule Running Log
            </Tab>
            <Tab tabid="tab-3">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-1">
              <DataPageArea title="Automation Rule">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right" v-if="!compList.editing">
                    <DropdownMenuItem @click="editAutomationRule" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.AutomationPermissionTypes.automation)">Edit Details</DropdownMenuItem>
                    <DropdownMenuItem @click="compList.editing = true" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.AutomationPermissionTypes.automation)">Edit Components</DropdownMenuItem>
                    <DropdownMenuItem @click="copyAutomationRule" icon="fa-copy" v-if="userHasCreatePermission(PermissionTypes.AutomationPermissionTypes.automation)">Copy Rule</DropdownMenuItem>
                    <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.AutomationPermissionTypes.automation) && userHasCreatePermission(PermissionTypes.AutomationPermissionTypes.automation)" />
                    <DropdownMenuItem @click="deleteAutomationRule" icon="fa-trash-alt" v-if="userHasDeletePermission(PermissionTypes.AutomationPermissionTypes.automation)">Delete Rule</DropdownMenuItem>
                    <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.AutomationPermissionTypes.automation) && isManualTriggerAvailable" />
                    <DropdownMenuItem @click="manualTriggerRule" icon="fa-wrench" v-if="isManualTriggerAvailable">Manual Trigger Rule</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{autoRule.name}}</DataItem>
                  <DataItem title="Description">{{autoRule.description}}</DataItem>
                  <DataItem title="Status">{{autoRule.isActive ? 'Enabled' : 'Disabled'}}</DataItem>
                </template>
              </DataPageArea>

              <br />
              <FormHorizontalRule />
              
              <ContentTitle>Automation Rule Components</ContentTitle>
              <ContentSubTitle>Structure for the automation processing</ContentSubTitle>

              <div class="p-2">
                <FormGroup :form="compList.form">
                  <AutomationRuleTree :editing="compList.editing" v-model="autoRule.autoRuleComponents" />

                  <FormSubmit v-if="compList.editing" label="Update" :submitting="compList.submitting" @submitted="submitComponentList" @cancelled="cancelEditingComponentList" />
                </FormGroup>
              </div>
            </TabPage>

            <TabPage tabid="tab-2">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Automation Rule Running Log</ContentTitle>
                  <ContentSubTitle>Recent list of automation logs from where the rule is executed</ContentSubTitle>

                  <StaticDataTable class="static-table-list" smallRows>
                    <template v-slot:header>
                      <tr>
                        <th>Type</th>
                        <th>Message</th>
                        <th>Details</th>
                        <th>Date</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="ruleLog of autoRuleLogList" :key="ruleLog.id">
                        <td>
                          <LabelBadge :labelClass="`label-autorule-logtype-${ruleLog.type}`">{{formatLookupName(ruleLog.type, AutomationLogTypeLookup)}}</LabelBadge>
                        </td>
                        <td>
                          <div>{{ruleLog.message}}</div>
                          <div v-if="ruleLog.type == AutomationLogTypes.communication">
                            <button @click.prevent="viewCommunicationLog(ruleLog.communicationLogId)" class="btn btn-default btn-xxs mt-2">
                              <i class="far fa-envelope-open-text"></i> View Communication
                            </button>
                          </div>
                        </td>
                        <td>{{ruleLog.details}}</td>
                        <td>{{formatDateTime(ruleLog.createdAt)}}</td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="autoRuleLogList.length == 0">No logs found</template>
                  </StaticDataTable>
                  
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-3">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.autoRule" :linkId="autoRule.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import AutomationMixin from '@/modules/automations/mixins/automations'
import FormMixin from '@/mixins/form'

import AutomationEditDialog from '@/modules/automations/views/automationEditDialog'
import AutomationRuleTree from '@/modules/automations/components/automationRuleTree'

import CommunicationLogDialog from '@/modules/communication/views/communicationLogDialog'

import { LinkTypes, AutomationComponentTypes, AutomationTriggerTypes, AutomationLogTypes, AutomationLogTypeLookup } from '@/store/types'

export default {
  name: 'AutomationRule',
  mixins: [AutomationMixin, FormMixin],
  components: {
    AutomationRuleTree
  },
  data() {
    return {
      compList: {
        form: {},
        editing: false,
        submitting: false
      },
      autoRule: {},
      autoRuleLogList: []
    }
  },
  computed: {
    isManualTriggerAvailable() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.AutomationPermissionTypes.automation))
        return false
      if(!this.autoRule.isActive)
        return false
      if(!_.isArray(this.autoRule.autoRuleComponents))
        return false

      // Rule needs manual trigger component
      return this.autoRule.autoRuleComponents.some(autoRuleComp => {
        return (autoRuleComp.autoComponentType.type == AutomationComponentTypes.trigger && autoRuleComp.autoComponentType.triggerType == AutomationTriggerTypes.manual)
      })
    },
    activityStreamURL() {
      if(!this.$route.params.id)
        return null
      return `/automations/rules/${this.$route.params.id}/activitystream`
    },
  },
  created() {
    this.AutomationLogTypes = AutomationLogTypes
    this.AutomationLogTypeLookup = AutomationLogTypeLookup
    this.LinkTypes = LinkTypes
  },
  mounted() {
    this.subscribeEndpoint(`/automations/rules/${this.$route.params.id}`, () => {
      this.reloadData()
    })

    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/automations/rules/${this.autoRule.id}`)
  },
  methods: {
    requestData() {

      this.pageLoadData(Promise.all([
        this.$apiService.automationService.getAutomationRule(this.$route.params.id),
        this.$apiService.automationService.getAutomationRuleLogList(this.$route.params.id)
      ]))
      .then(results => {
        this.autoRule = results[0].data
        this.autoRuleLogList = results[1].data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    reloadData() {
      // Ignore reload if editing
      if(this.compList.editing)
        return
      
      if(this.$refs.activityStream)
        this.$refs.activityStream.reloadData()

      this.requestData()
    },
    submitComponentList() {
      this.compList.form = this.clearFormResponse()

      var updateData = {
        autoRuleComponents: this.prepareComponentListForUpdate(this.autoRule.autoRuleComponents)
      }

      this.compList.submitting = true
      this.$apiService.automationService.updateAutomationRule(this.autoRule.id, updateData)
      .then(response => {
        this.compList.editing = false

        this.successToast('Automation Components Updated')
        this.reloadData()
      })
      .catch(err => {
        this.compList.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.compList.submitting = false
      })

    },
    cancelEditingComponentList() {
      this.compList.editing = false
      this.compList.form = this.clearFormResponse()

      this.reloadData()
    },
    editAutomationRule() {
      this.displayModal(AutomationEditDialog, {
        autoRule: this.autoRule
      })
      .then(success => {
        if(success) {
          this.successToast('Automation Updated')
          this.reloadData()
        }
      })
    },
    copyAutomationRule() {
      this.$router.push({
        name: 'NewAutomationRule',
        params: {
          copyFromRule: true,
          sourceAutoRuleId: parseInt(this.autoRule.id)
        }
      })
    },
    deleteAutomationRule() {
      this.confirmDeleteMessage('Delete Automation Rule?', 'This will delete the rule. This action cannot be undone')
      .then(confirm => {
        if(confirm) {
          this.$apiService.automationService.deleteAutomationRule(this.autoRule.id)
          .then(response => {
            this.successToast('Automation Rule Deleted')
            this.$router.push('/automations/rules')
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error deleting rule')
          })
        }
      })
    },
    manualTriggerRule() {
      this.confirmMessage('Run Manual Trigger?', 'This will execute the rule & all actions')
      .then(confirm => {
        if(confirm) {
          this.$apiService.automationService.trigerAutomationRule(this.autoRule.id)
          .then(response => {
            this.successToast('Automation Rule Trigger Requested')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error running rule')
          })
        }
      })
    },
    viewCommunicationLog(commLogId)  {
      if(!commLogId)
        return

      this.displayModal(CommunicationLogDialog, { 
        communicationLogId: commLogId
      })
      .then(() => { })
    },
  }

  
}
</script>